import { supabase } from '@/lib/supabase'
import { customAlphabet } from 'nanoid'
import type { JsonValue } from '../types'

export type GraphDocument = {
  id: string
  owner_id: string
  title: string
  content: JsonValue
  is_public: boolean
  share_slug: string | null
  created_at: string
  updated_at: string
}

export type DocumentSummary = Pick<
  GraphDocument,
  'id' | 'title' | 'is_public' | 'share_slug' | 'created_at' | 'updated_at'
>

const TABLE = 'graph_documents'

// URL-safe slug alphabet (10 chars ~ 1e15 space)
const makeSlug = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 10)

export async function listMyDocuments(): Promise<DocumentSummary[]> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, title, is_public, share_slug, created_at, updated_at')
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as DocumentSummary[]
}

export async function getDocument(id: string): Promise<GraphDocument | null> {
  const { data, error } = await supabase.from(TABLE).select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return (data ?? null) as GraphDocument | null
}

export async function getPublicDocumentBySlug(slug: string): Promise<GraphDocument | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('share_slug', slug)
    .eq('is_public', true)
    .maybeSingle()
  if (error) throw error
  return (data ?? null) as GraphDocument | null
}

export async function createDocument(initial: {
  title?: string
  content?: JsonValue
}): Promise<GraphDocument> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()
  if (userError) throw userError
  if (!user) throw new Error('Not authenticated')

  const { data, error } = await supabase
    .from(TABLE)
    .insert({
      owner_id: user.id,
      title: initial.title ?? 'Untitled',
      content: initial.content ?? {},
    })
    .select('*')
    .single()
  if (error) throw error
  return data as GraphDocument
}

export async function patchDocument(
  id: string,
  patch: Partial<Pick<GraphDocument, 'title' | 'content'>>,
): Promise<GraphDocument> {
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data as GraphDocument
}

export async function deleteDocument(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('id', id)
  if (error) throw error
}

export async function setShared(id: string, enabled: boolean): Promise<GraphDocument> {
  const patch: Partial<GraphDocument> = enabled
    ? { is_public: true, share_slug: makeSlug() }
    : { is_public: false, share_slug: null }
  const { data, error } = await supabase
    .from(TABLE)
    .update(patch)
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data as GraphDocument
}

export async function rotateShareSlug(id: string): Promise<GraphDocument> {
  const { data, error } = await supabase
    .from(TABLE)
    .update({ share_slug: makeSlug(), is_public: true })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw error
  return data as GraphDocument
}
